import { ChevronRightIcon } from "@heroicons/react/outline";
import { useState } from "react";

import useLabel from "@apps/use-label/use-label";
import { classNames } from "@helpers/css";

export type ToolCallInfo = {
  toolName: string;
  resultCount?: number | null;
  arguments?: string | null;
  output?: string | null;
};

const ToolCallDetail = ({ tc, label }: { tc: ToolCallInfo; label: string }) => {
  const [expanded, setExpanded] = useState(false);
  const hasDetail = tc.arguments || tc.output;

  return (
    <li className="text-xs text-gray-400">
      {hasDetail ? (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1 hover:text-gray-600 transition-colors text-left"
        >
          <ChevronRightIcon
            className={classNames(
              "h-2.5 w-2.5 shrink-0 transition-transform",
              expanded && "rotate-90",
            )}
          />
          <span>{label}</span>
        </button>
      ) : (
        <span className="ml-3.5">{label}</span>
      )}
      {expanded && (
        <div className="ml-3.5 mt-1 flex flex-col gap-1.5 mb-1.5">
          {tc.arguments && (
            <div>
              <div className="text-gray-500 font-medium mb-0.5">Parameters</div>
              <pre className="text-[11px] bg-gray-50 rounded px-2 py-1 overflow-x-auto whitespace-pre-wrap break-all text-gray-500">
                {formatJson(tc.arguments)}
              </pre>
            </div>
          )}
          {tc.output && (
            <div>
              <div className="text-gray-500 font-medium mb-0.5">Response</div>
              <pre className="text-[11px] bg-gray-50 rounded px-2 py-1 overflow-x-auto whitespace-pre-wrap break-all text-gray-500 max-h-48 overflow-y-auto">
                {formatJson(tc.output)}
              </pre>
            </div>
          )}
        </div>
      )}
    </li>
  );
};

function formatJson(str: string): string {
  try {
    return JSON.stringify(JSON.parse(str), null, 2);
  } catch {
    return str;
  }
}

const AskAIToolCalls = ({ toolCalls }: { toolCalls: ToolCallInfo[] }) => {
  const [expanded, setExpanded] = useState(false);
  const label = useLabel();

  if (toolCalls.length === 0) return null;

  const getToolLabel = (tc: ToolCallInfo) => {
    const count = tc.resultCount;
    const hasCount = count != null;

    if (tc.toolName === "list_goals") {
      const name = label("goal", { pluralize: !hasCount || count !== 1 });
      return hasCount ? `Fetched ${count} ${name}` : `Fetched ${name}`;
    }
    if (tc.toolName === "list_goal_checkins") {
      return hasCount
        ? `Fetched ${count} goal check-in${count !== 1 ? "s" : ""}`
        : "Fetched goal check-ins";
    }
    if (tc.toolName === "list_action_items") {
      return hasCount
        ? `Fetched ${count} action item${count !== 1 ? "s" : ""}`
        : "Fetched action items";
    }
    if (tc.toolName === "list_feedback") {
      const name = label("feedback", { pluralize: !hasCount || count !== 1 });
      return hasCount ? `Fetched ${count} ${name}` : `Fetched ${name}`;
    }
    if (tc.toolName === "list_recognitions") {
      const name = label("recognition", {
        pluralize: !hasCount || count !== 1,
      });
      return hasCount ? `Fetched ${count} ${name}` : `Fetched ${name}`;
    }
    if (tc.toolName === "list_meetings") {
      const name = label("meeting", { pluralize: !hasCount || count !== 1 });
      return hasCount ? `Fetched ${count} ${name}` : `Fetched ${name}`;
    }
    if (tc.toolName === "list_assessments") {
      return hasCount
        ? `Fetched ${count} assessment${count !== 1 ? "s" : ""}`
        : "Fetched assessments";
    }
    if (tc.toolName === "get_user_infos") {
      return "Fetched user info";
    }
    if (tc.toolName === "query_external_events") {
      return hasCount
        ? `Fetched ${count} external event${count !== 1 ? "s" : ""}`
        : "Fetched external events";
    }
    if (tc.toolName === "load_documentation") {
      return "Loaded documentation";
    }
    return tc.toolName;
  };

  return (
    <div className="text-sm text-gray-500">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 hover:text-gray-700 transition-colors"
      >
        <ChevronRightIcon
          className={classNames(
            "h-3 w-3 transition-transform",
            expanded && "rotate-90",
          )}
        />
        <span>
          {toolCalls.length} tool call{toolCalls.length !== 1 ? "s" : ""}
        </span>
      </button>
      {expanded && (
        <ul className="ml-4 mt-1 flex flex-col gap-0.5">
          {toolCalls.map((tc, index) => (
            <ToolCallDetail key={index} tc={tc} label={getToolLabel(tc)} />
          ))}
        </ul>
      )}
    </div>
  );
};

export default AskAIToolCalls;
